import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import { createAdminNotificationMeta } from '../../utils/adminNotificationMeta';
import { v4 as uuidv4 } from 'uuid';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { formatCustomTagsToLaTeX } from '../common/QuestionFormatter';
import useExamProtection from '../../utils/useExamProtection';
import MathTextRenderer from '../common/MathTextRenderer';

// MathJax configuration
const config = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    inlineMath: [["$", "$"], ["\\(", "\\)"]],
    displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    processEscapes: true,
    macros: {
      '\\ce': '{\\text{#1}}'
    },
  },
  options: {
    enableMenu: false,
  },
};

function Quiz() {
  const { quizId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Get participantId from URL or generate new one
  const participantId = searchParams.get('participant') || uuidv4();

  // Redirect to include participantId if not in URL
  useEffect(() => {
    if (!searchParams.get('participant')) {
      navigate(`/take-quiz/${quizId}?participant=${participantId}`, { replace: true });
    }
  }, [quizId, participantId, searchParams, navigate]);

  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [participantInitialized, setParticipantInitialized] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentFlaggedQuestion, setCurrentFlaggedQuestion] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const flagReasons = [
    'Incorrect answer',
    'Unclear question',
    'Grammatical error',
    'Technical issue',
    'Other'
  ];

  console.log('Quiz render:', { quizId, participantId, currentUser: !!currentUser, loading, quiz: !!quiz, timeRemaining });
  // Optionally, add the states if you want to display messages:
  const [violationCount, setViolationCount] = useState(0);
  const [violationMessage, setViolationMessage] = useState('');

  useExamProtection({
    enabled: !!currentUser && !!quiz,
    getState: () => ({ answers, timeLeft: timeRemaining }),
    // No generic saveDraft for now since we don't use course/module
    onViolation: (type, count) => {
      setViolationCount(count);
      setViolationMessage('Exam tab lost focus or hidden. Please stay on this page.');
    },
    onAutoSubmit: () => {
      handleSubmit();
    },
    autosaveMs: 15000,
    maxViolationsBeforeAutoSubmit: 4,
    warnBeforeUnload: true,
    countOnBlur: false,
    violationDebounceMs: 30000,
    violationThrottleMs: 10000,
  });
  // Modify the initialization useEffect
  useEffect(() => {
    const initializeParticipant = async () => {
      if (!quizId || !participantId || !currentUser) return;

      try {
        const participantRef = doc(db, 'public-quiz-participants', participantId);
        const participantDoc = await getDoc(participantRef);

        if (!participantDoc.exists()) {
          // Create new participant document if it doesn't exist
          await setDoc(participantRef, {
            quizId: quizId,
            userId: currentUser.uid,
            startedAt: serverTimestamp(),
            completed: false,
            answers: {}
          });
          console.log('Created new participant document');
        } else {
          // If already completed, redirect to results
          if (participantDoc.data().completed) {
            const score = participantDoc.data().score;
            navigate(`/quiz-results/${quizId}?participant=${participantId}&score=${score}`);
            return;
          }
          // If not completed, load existing answers if any
          const existingAnswers = participantDoc.data().answers || {};
          setAnswers(existingAnswers);
        }
        setParticipantInitialized(true);
      } catch (err) {
        console.error('Error initializing participant:', err);
        setError('Failed to initialize quiz. Please try again.');
      }
    };

    initializeParticipant();
  }, [quizId, participantId, navigate, currentUser]);

  // Fetch quiz data
  useEffect(() => {
    let isMounted = true;

    async function fetchQuiz() {
      if (!quizId) return;

      console.log('Fetching quiz:', quizId);
      try {
        const docRef = doc(db, 'quizzes', quizId);
        const docSnap = await getDoc(docRef);

        if (!isMounted) return;

        if (docSnap.exists()) {
          const quizData = docSnap.data();
          setQuiz(quizData);
          console.log('Quiz found');
        } else {
          console.log('Quiz not found');
          setError('Quiz not found');
        }
      } catch (err) {
        console.error('Error fetching quiz:', err);
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchQuiz();

    return () => {
      isMounted = false;
    };
  }, [quizId]); // Only depend on quizId

  // Wrap handleSubmit in useCallback
  const handleSubmit = useCallback(async () => {
    try {
      // Calculate score
      let score = 0;
      Object.entries(answers).forEach(([questionIndex, selectedAnswer]) => {
        const question = quiz.questions[questionIndex];
        if (question && parseInt(selectedAnswer, 10) === question.correctOption) {
          score++;
        }
      });

      const finalScore = Math.round((score / quiz.questions.length) * 100);

      // Check if participant exists
      const participantRef = doc(db, 'public-quiz-participants', participantId);
      const participantDoc = await getDoc(participantRef);

      if (!participantDoc.exists()) {
        throw new Error('Participant not found');
      }

      // Update participant record - include userId to match Firestore rules
      await updateDoc(participantRef, {
        completed: true,
        score: finalScore,
        answers: answers,
        completedAt: serverTimestamp(),
        userId: currentUser.uid
      });

      // Navigate to results
      navigate(`/quiz-results/${quizId}?participant=${participantId}&score=${finalScore}`);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setError('Failed to submit quiz. Please try again.');
    }
  }, [answers, quiz, participantId, quizId, navigate, currentUser.uid]);

  // Now the effect can safely include handleSubmit
  useEffect(() => {
    if (isTimeUp) {
      handleSubmit();
    } else if (quiz && timeRemaining !== null && timeRemaining <= 0) {
      // Also submit if time runs out directly
      handleSubmit();
    }
  }, [isTimeUp, handleSubmit, quiz, timeRemaining]);

  // Add console logs to debug timer
  useEffect(() => {
    if (!quiz?.timeLimit || !participantInitialized) {
      console.log('Timer not starting:', { timeLimit: quiz?.timeLimit, participantInitialized });
      return;
    }

    let timer;
    let cancelled = false;

    const initTimer = async () => {
      try {
        const participantRef = doc(db, 'public-quiz-participants', participantId);
        const participantDoc = await getDoc(participantRef);
        if (!participantDoc.exists()) {
          console.error('Participant document not found');
          return;
        }

        const startTime = participantDoc.data().startedAt?.toDate();
        if (!startTime) {
          console.error('Start time not found in participant document');
          return;
        }

        const timeLimitMs = quiz.timeLimit * 60 * 1000; // Convert minutes to milliseconds
        const endTime = new Date(startTime.getTime() + timeLimitMs);
        const remaining = endTime - new Date();

        console.log('Timer initialized:', { startTime, timeLimitMs, endTime, remaining });

        if (cancelled) return;

        // Set initial state
        setTimeRemaining(Math.max(0, remaining));

        if (remaining <= 0 && !participantDoc.data().completed) {
          setIsTimeUp(true);
          return; // do not start interval
        }

        // Start ticking without relying on stale state
        timer = setInterval(() => {
          setTimeRemaining(prev => {
            const current = typeof prev === 'number' ? prev : Math.max(0, remaining - 1000);
            const next = Math.max(0, current - 1000);
            if (next === 0) {
              setIsTimeUp(true);
              if (timer) clearInterval(timer);
            }
            return next;
          });
        }, 1000);
      } catch (err) {
        console.error('Error getting time remaining:', err);
      }
    };

    initTimer();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [quiz?.timeLimit, participantInitialized, participantId]);

  // Add this function to handle flagging questions
  const handleFlagQuestion = (questionId) => {
    setCurrentFlaggedQuestion(questionId);
    setShowFlagModal(true);
  };

  // Add this function to submit the flag
  const submitFlag = async () => {
    if (!flagReason || !auth.currentUser) return; // Ensure currentUser exists

    try {
      await addDoc(collection(db, 'flagged_questions'), {
        questionId: currentFlaggedQuestion,
        userId: auth.currentUser.uid,
        reason: flagReason,
        timestamp: serverTimestamp(),
        status: 'pending',
        quizId: quizId,
        questionNumber: currentQuestion + 1,
        userAnswer: answers[currentQuestion] !== undefined ? answers[currentQuestion] : null, // Handle no answer
        correctAnswer: quiz.questions[currentQuestion]?.correctOption, // Handle missing question/option
        ...createAdminNotificationMeta({
          type: 'flagged_question',
          summary: `Flagged question in quiz ${quizId} by ${auth.currentUser.displayName || auth.currentUser.email || 'a user'}`,
        }),
      });

      setFlaggedQuestions(prev => new Set([...prev, currentFlaggedQuestion]));
      setShowFlagModal(false);
      setFlagReason('');
      setCurrentFlaggedQuestion(null);
    } catch (error) {
      console.error('Error submitting flag:', error);
    }
  };

  // Helper function to format math expressions
  const formatMath = (text) => {
    if (!text) return '';

    // Handle fractions
    text = text.replace(/fraq{([^}]+)}{([^}]+)}/g, '\\frac{$1}{$2}');

    // Handle square roots
    text = text.replace(/sqrt{([^}]+)}/g, '\\sqrt{$1}');

    // Handle powers
    text = text.replace(/\^(\d+)/g, '^{${1}}');

    // Handle subscripts
    text = text.replace(/_(\d+)/g, '_{${1}}');

    // Handle chemical formulas
    text = text.replace(/([A-Z][a-z]?\d*)/g, '\\ce{$1}');

    return text;
  };

  // Modify the loading check in the render
  if (loading || !participantInitialized) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!quiz?.questions) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div>No quiz questions found</div>
      </div>
    );
  }



  return (
    <MathJaxContext config={config}>
      <div className="max-w-3xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Question {currentQuestion + 1} of {quiz.questions.length}</h1>
            {timeRemaining !== null && (
              <div className={`text-xl font-bold ${timeRemaining < 60000 ? 'text-red-600' : ''}`}>
                Time: {Math.floor(timeRemaining / 60000)}:{String(Math.floor((timeRemaining % 60000) / 1000)).padStart(2, '0')}
              </div>
            )}
          </div>

          {showSubmitConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-xl font-bold mb-4">Submit Quiz</h3>
                <p>Are you sure you want to submit your answers? You will not be able to change them afterwards.</p>
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowSubmitConfirm(false)}
                    className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowSubmitConfirm(false);
                      handleSubmit();
                    }}
                    className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
                  >
                    Yes, Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tracker UI */}
          {quiz && quiz.questions && (
            <div className="flex justify-center gap-8 mb-6">
              <div className="bg-gray-100 rounded px-4 py-2 text-center">
                <div className="text-xs text-gray-500">Answered</div>
                <div className="font-bold text-lg">{Object.keys(answers).length}</div>
              </div>
              <div className="bg-gray-100 rounded px-4 py-2 text-center">
                <div className="text-xs text-gray-500">Skipped</div>
                <div className="font-bold text-lg">{quiz.questions.length - Object.keys(answers).length}</div>
              </div>
              <div className="bg-gray-100 rounded px-4 py-2 text-center">
                <div className="text-xs text-gray-500">Total</div>
                <div className="font-bold text-lg">{quiz.questions.length}</div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <p className="text-lg">
              <MathTextRenderer text={formatCustomTagsToLaTeX(quiz.questions[currentQuestion].question)} />
            </p>
          </div>

          <div className="space-y-3" key={currentQuestion}>
            {quiz.questions[currentQuestion].options.map((option, index) => (
              <label
                key={index}
                className={`block p-3 border rounded cursor-pointer
                ${answers[currentQuestion] === index ? 'bg-[rgb(130,88,18)] text-white' : 'hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="answer"
                  value={index}
                  checked={answers[currentQuestion] === index}
                  onChange={() => {
                    setAnswers(prev => ({
                      ...prev,
                      [currentQuestion]: index
                    }));
                  }}
                  className="hidden"
                />
                <span>
                  <MathTextRenderer text={formatCustomTagsToLaTeX(option)} />
                </span>
              </label>
            ))}
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>

            {currentQuestion === quiz.questions.length - 1 ? (
              <button
                onClick={() => setShowSubmitConfirm(true)}
                className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
              >
                Submit Quiz
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestion(prev => Math.min(quiz.questions.length - 1, prev + 1))}
                className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            )}
          </div>

          <button
            onClick={() => handleFlagQuestion(quiz.questions[currentQuestion].id)}
            className={`text-sm mt-4 ${flaggedQuestions.has(quiz.questions[currentQuestion].id)
                ? 'text-red-500 cursor-not-allowed'
                : 'text-gray-400 hover:text-red-500'
              }`}
            disabled={flaggedQuestions.has(quiz.questions[currentQuestion].id)}
          >
            <svg
              className="w-5 h-5 inline-block mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
              />
            </svg>
            {flaggedQuestions.has(quiz.questions[currentQuestion].id) ? 'Question Flagged' : 'Flag Question'}
          </button>
        </div>

        {showFlagModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold mb-4">Flag Question</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Flagging
                </label>
                <select
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                >
                  <option value="">Select a reason</option>
                  {flagReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowFlagModal(false);
                    setFlagReason('');
                    setCurrentFlaggedQuestion(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={submitFlag}
                  disabled={!flagReason}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Submit Flag
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
}

export default Quiz; 